import type { Passport } from '~/types'

export default defineNuxtPlugin(() => {
  const fetchWithToken = async <T>(url: string, options: Record<string, any> = {}): Promise<T> => {
    const store = useCustomerStore()
    const token = store.accessToken
    // `Accept` is not cosmetic. Nitro's error handler only emits a JSON error when
    // `isJsonRequest(event)`, which auto-accepts `/api/**` and `*.json` but NOT the
    // `/cache/api/**` prefix. Without this header a 401 from the API gate on a
    // `/cache/` path made Nitro run a whole nested `/__nuxt_error` page render inside
    // the in-flight SSR render — which pushed the API path through the Vue router
    // ("No match found for location") and destroyed the outer request's async context.
    const headers: Record<string, string> = { Accept: 'application/json', ...(options.headers ?? {}) }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Forward the opaque marketing-email recipient token (captured from ?nlhash into a
    // cookie) so the backend can attach the recipient's email to the cart for
    // abandoned-cart automations. Omitted entirely when the cookie has no value.
    const nlhash = useCookie<string | null>('nlhash').value
    if (nlhash) headers['X-Nl-Hash'] = nlhash

    let data: T
    try {
      data = await $fetch<T>(url, { ...options, headers })
    } catch (err: any) {
      // Only a request that actually carried a token can be rescued by a refresh.
      // Without this, an anonymous call hitting the API gate's 401 would trigger a
      // refresh with no refresh token — which now logs out, i.e. tears down a
      // session that a *different* tab may have just established.
      if (err?.status === 401 && options.retry !== 0 && token) {
        // `store.refresh()` de-duplicates: N parallel 401s await ONE refresh call.
        // Firing N refreshes with the same single-use refresh token would make
        // calls 2..N fail and log the user out mid-session.
        try {
          await store.refresh()
        } catch {
          throw err
        }

        const newToken = store.accessToken

        data = await $fetch<T>(url, {
          ...options,
          headers: { ...headers, Authorization: `Bearer ${newToken}` },
        })
      } else {
        throw err
      }
    }

    if (typeof data === 'string') {
      throw new Error(`Invalid JSON response: ${(data as string).substring(0, 100)}`)
    }

    return data
  }

  return {
    provide: { fetchWithToken },
  }
})
